import { HttpErrorResponse } from '@angular/common/http';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Reducers} from '@app/reducers';
import {AssociationRoleDialogComponent} from '@clients/components/associations/association-role-dialog/association-role-dialog.component';
import {AssociationTariffDialogComponent} from '@clients/components/associations/association-tariff-dialog/association-tariff-dialog.component';
import {BuildingDialogComponent} from '@clients/components/associations/building-dialog/building-dialog.component';
import {GarageRowDialogComponent} from '@clients/components/associations/garage-row-dialog/garage-row-dialog.component';
import {ConfigService} from '@config/config.service';
import {environment} from '@environments/environment';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import {select, Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {OAuth2TokensDto} from '@services/authentication/model/oAuth2TokensDto';
import {ConfirmationDialogComponent} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {PermissionsModel} from '@shared/models/permission.model';
import {AssociationsService} from '@shared/services/associations.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, EMPTY, Observable, of, Subject, takeUntil} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import {UntypedFormBuilder, UntypedFormGroup} from "@angular/forms";
import * as associationActions from "@clients/ngrx/actions/associations.actions";
import {Actions, ofType} from "@ngrx/effects";
import {UploadApartmentsDialogComponentNew} from "@clients/components/associations/upload-apartments-dialog-new/upload-apartments-dialog.component-new";
import {OrganizationManagerModel} from "@services/back-office/model/organizationManagerModel";
import {PageOrganizationManagerModel} from "@services/back-office/model/pageOrganizationManagerModel";
import {AssociationManagerDialogComponent} from "@clients/components/associations/association-manager-dialog/association-manager-dialog.component";
import {AssociationTariffHistoryComponent} from "@clients/components/associations/association-tariff-history/association-tariff-history.component";
import {BillingService} from "@shared/services/billing.service";
import {BillingContractModel} from "@services/back-office/model/billingContractModel";
import {ListItemType} from "@shared/enum/list-item-type";
import * as contractActions from '@billing/ngrx/actions/contracts.actions';
import * as contactActions from '@billing/ngrx/actions/contracts.actions';
import {OpenApiService} from "@shared/services/openApi.service";
import {GovernmentModel} from "@services/back-office/model/governmentModel";
import {OrganizationTariffModel} from "@services/back-office/model/organizationTariffModel";
import {MapPolygonDialogComponent} from '@shared/components/map-module/map-polygon/map-polygon-dialog.component';
import * as QRCode from 'qrcode';
import {OrganizationBuildingModel} from '@services/back-office/model/organizationBuildingModel';
import {OrganizationSectionModel} from '@services/back-office/model/organizationSectionModel';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import * as associationsActions from '@clients/ngrx/actions/associations.actions';
import {AssociationDialogComponent} from '@clients/components/associations/association-dialog/association-dialog.component';

@Component({
    selector: 'app-association-view-dialog',
    templateUrl: './association-view-dialog.component.html',
    styleUrls: ['./association-view-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AssociationViewDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    /** Tables */
    buildingDataSource: MatTableDataSource<OrganizationBuildingModel> = new MatTableDataSource<OrganizationBuildingModel>();
    buildingColumns: string[] = ['address', 'map', 'actions'];
    garageRowDataSource: MatTableDataSource<OrganizationSectionModel> = new MatTableDataSource<OrganizationSectionModel>();
    garageRowColumns: string[] = ['address', 'actions'];
    firstTabLoaded = false;
    roleDataSource: MatTableDataSource<GovernmentModel> = new MatTableDataSource<GovernmentModel>();
    roleColumns: string[] = ['user', 'roleType', 'role', 'actions'];
    secondTabLoaded = false;
    contractsDataSource: MatTableDataSource<BillingContractModel> = new MatTableDataSource<BillingContractModel>();
    columns: string[] = ['name', 'amount', 'actions'];
    thirdTabLoaded = false;
    managerDataSource: MatTableDataSource<OrganizationManagerModel> = new MatTableDataSource<OrganizationManagerModel>();
    managerColumns: string[] = ['manager', 'role', 'actions'];
    managersTabLoaded = false;
    canAddMainAdmin = false;
    /** Form */
    associationForm: UntypedFormGroup;
    saved = false;
    canAddContractSaas = true;
    canAddContractAcc = true;
    canAddContractPersonal = true;
    public environment = environment;
    @ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            association: AssociationListResponse,
            disableAddContract?: boolean
        },
        public dialogRef: MatDialogRef<AssociationViewDialogComponent>,
        private associationsService: AssociationsService,
        private openApi: OpenApiService,
        private billingService: BillingService,
        private changeDetectorRef: ChangeDetectorRef,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private sharedService: SharedService,
        private config: ConfigService,
        private store$: Store<Reducers>,
        private formBuilder: UntypedFormBuilder,
        private actions$: Actions,
    ) {
        /** Permissions */
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        /** Get Permissions from Observable and Filter Navigation Items */
        this.getPermissions();
        this.tabChanged({index: 0});

        if (!this.associationForm) {
            this.initForm();
        }

        this.watchEditAssociationSuccess();
        this.watchContractActions();

        if (this.data.association.type === 'HOUSING_OFFICE') {
            this.buildingColumns.splice(1, 0, 'link');
        }
    }

    ngOnDestroy() {
        this.destroyed$.complete();
        this.loading$.complete();
    }

    initForm() {
        this.associationForm = this.formBuilder.group({});
    }

    patchForm() {
        if (!this.associationForm) {
            this.initForm();
        }
        this.associationForm.patchValue({});
    }

    updateAssociation() {
        this.associationsService.getAssociation(this.data.association.id!).pipe(
            takeUntil(this.destroyed$),
            map((res: AssociationListResponse) => {
                this.data.association = res;
                this.store$.dispatch(new associationActions.EditAssociationsSuccess({...res, apiToken: res?.apiToken ? res?.apiToken : ''}));
            })
        ).subscribe();
    }

    /** Get Permissions from Observable and Filter Navigation Items */
    getPermissions() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );
    }

    /** Check Permissions for DOM Elements */
    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    checkAllPermissions(): boolean {
        return this.permissions ? SharedService.checkAllPermissions(this.permissions[0].permissions) : false;
    }

    tabChanged(event: any) {
        switch (event.index) {
            case 0:
                if (!this.firstTabLoaded) {
                    this.getBuildings();
                    this.data.association.type === 'GARAGE_COOP' ? this.getGarageRows() : null;
                }
                if (!this.saved) {
                    this.patchForm();
                }
                break;
            case 1:
                if (!this.secondTabLoaded) {
                    this.getRoles();
                }
                break;
            case 2:
                if (!this.thirdTabLoaded) {
                    this.getContracts();
                }
                break;
            case 3:
                if (!this.managersTabLoaded) {
                    this.getManagers();
                }
                break;
            default:
                return;
        }
    }

    /** Garage Rows */
    getGarageRows() {
        this.loading$.next(true);
        this.associationsService.loadSections(this.data.association.id!).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: OrganizationSectionModel[]) => {
                this.garageRowDataSource.data = result;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.firstTabLoaded = true;
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    editGarageRow(item: OrganizationSectionModel) {
        const garageRowDialog$ = this.sharedService.openDialog(
            GarageRowDialogComponent,
            {...this.config.addItemDialog, maxWidth: '400px', height: 'auto', hasBackdrop: true},
            {
                association: this.data.association,
                garageRow: item,
            }
        );

        garageRowDialog$.afterClosed().pipe(
            take(1)
        ).subscribe((dialogData: { result: boolean, data?: OrganizationSectionModel }) => {
            if (dialogData?.result && dialogData?.data) {
                const garageRows = this.garageRowDataSource.data;
                if (garageRows && garageRows.length) {
                    const garageRowIndex = garageRows.findIndex((item: any) => item.id === dialogData.data!.id);
                    if (garageRowIndex !== -1) {
                        garageRows.splice(garageRowIndex, 1, dialogData.data);
                        this.garageRowDataSource.data = garageRows;
                    }
                }
            }
        });
    }

    deleteGarageRow(item: OrganizationSectionModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.clients.associations.garageRow.deleteGarageRowWarning',
                    {name: item.name}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.loading$.next(true);
                this.associationsService.deleteSection(item.id!).pipe(
                    map(() => {
                        this.loading$.next(false);
                        const garageRows = this.garageRowDataSource.data;
                        if (garageRows && garageRows.length) {
                            const garageRowIndex = garageRows.findIndex((garage: OrganizationSectionModel) => garage.id === item.id);
                            if (garageRowIndex !== -1) {
                                garageRows.splice(garageRowIndex, 1);
                                this.garageRowDataSource.data = garageRows;
                            }
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.message}`
                        );
                        this.loading$.next(false);
                        return EMPTY;
                    })
                ).subscribe();
            }
        });

    }

    /** Buildings */
    getBuildings() {
        this.loading$.next(true);
        this.associationsService.loadBuildings(this.data.association.id!).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: OrganizationBuildingModel[]) => {
                this.buildingDataSource.data = result;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.firstTabLoaded = true;
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    uploadData(building?: any) {
        this.sharedService.openDialog(
            UploadApartmentsDialogComponentNew,
            {...this.config.addItemDialog, height: 'auto', maxWidth: '500px', hasBackdrop: true},
            {
                association: this.data.association,
                building: building
            });
    }

    editBuilding(building: OrganizationBuildingModel) {
        // eslint-disable-next-line no-case-declarations
        const buildingDialog$ = this.sharedService.openDialog(
            BuildingDialogComponent,
            {...this.config.addItemDialog, maxWidth: '700px', hasBackdrop: true},
            {
                association: this.data.association,
                building: building
            }
        );

        buildingDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((dialogData) => {
            if (dialogData.result && dialogData.data) {
                const buildings: OrganizationBuildingModel[] = this.buildingDataSource.data;
                const buildingIndex: number = buildings && buildings.length ? buildings.findIndex((item: OrganizationBuildingModel) => item.id === dialogData.data.id) : -1;
                if (buildingIndex !== -1) {
                    buildings.splice(buildingIndex, 1, {...dialogData.data, name: dialogData.data.name});
                    this.buildingDataSource.data = buildings;
                    this.changeDetectorRef.detectChanges();
                }

                this.updateListItemLocation();
            }
        });
    }

    deleteBuilding(building: OrganizationBuildingModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.clients.associations.associationViewDialog.deleteBuildingWarning',
                    {name: building.name}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.loading$.next(true);
                this.associationsService.deleteBuilding(building.id!).pipe(
                    take(1),
                    map(() => {
                        this.toastrService.info('Будинок видалено');
                        // прибираємо будинок локально, щоб коректно перерахувати геодані обʼєднання
                        this.buildingDataSource.data = this.buildingDataSource.data.filter((item: OrganizationBuildingModel) => item.id !== building.id);
                        this.changeDetectorRef.detectChanges();
                        this.updateListItemLocation();
                        // перечитуємо список будинків з сервера
                        this.getBuildings();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error?.message}`
                        );
                        this.loading$.next(false);
                        return EMPTY;
                    })
                ).subscribe();
            }
        });
    }

    /** Roles */
    getRoles() {
        this.loading$.next(true);
        this.associationsService.loadRoles(this.data.association.id!).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: GovernmentModel[]) => {
                this.roleDataSource.data = result;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.secondTabLoaded = true;
                this.getCanAddAdmin(result);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    editMainRole(element: GovernmentModel) {
        const associationRoleDialog$ = this.sharedService.openDialog(
            AssociationRoleDialogComponent,
            {...this.config.addItemDialog, maxWidth: '400px', hasBackdrop: true},
            {
                association: this.data.association,
                formType: 'setGroupMainAdmin',
                admin: element
            }
        );

        associationRoleDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((dialogData: { result: boolean, data?: any }) => {
            if (dialogData?.result) {
                this.getRoles();
            }
        });
    }

    deleteRole(element: GovernmentModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.clients.associations.associationViewDialog.deleteRoleWarning',
                    {name: element.user?.userName?.fullName}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe((result) => {
            if (result) {
                this.associationsService.deleteGovernment(element.id!).pipe(
                    take(1),
                    map(() => {
                        const positions = this.roleDataSource.data;
                        if (positions && positions.length) {
                            const positionIndex = positions.findIndex((item: GovernmentModel) => item.user?.userId === element.user?.userId);

                            if (positionIndex !== -1) {
                                positions.splice(positionIndex, 1);
                                this.roleDataSource.data = positions;
                                this.changeDetectorRef.detectChanges();
                            }
                        }

                        if (this.data.association.tariff == 'SOCIAL') {
                            this.updateAssociation();
                        }

                        this.getCanAddAdmin(positions);
                    })
                ).subscribe();
            }
        });
    }

    getCanAddAdmin(governments: GovernmentModel[]) {
        this.canAddMainAdmin = !governments.find(el => el.admin);
    }

    /** Contracts */
    getContracts() {
        this.billingService.getAssociationContracts(this.data.association.id!).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: BillingContractModel[]) => {
                // Договір певного типу можна додати, лише якщо немає іншого не скасованого договору цього типу
                // без counterParty. Не скасований договір із counterParty не блокує додавання нового.
                // Перераховуємо щоразу, щоб після видалення/скасування договору кнопка знову з'являлась
                const hasActive = (type: string) => !!result?.some(el =>
                    el.type === type && el.status !== 'CANCELLED' && !el.counterParty
                );
                this.canAddContractSaas = !hasActive('SAAS');
                this.canAddContractAcc = !hasActive('ACCOUNTING');
                this.canAddContractPersonal = !hasActive('PERSONAL_ACCOUNTING');
                this.contractsDataSource.data = result;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.thirdTabLoaded = true;
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    addContract(type: string) {
        this.sharedService.createCrmItem(ListItemType.CONTRACT, {association: this.data.association, type: type}, true);
    }

    editContract(item: BillingContractModel) {
        this.sharedService.openCrmItem(ListItemType.CONTRACT, item.id!);
    }

    deleteContract(item: BillingContractModel) {
        this.sharedService.loadLazyModule(() => {
            return import('@app/billing/billing-v2.module').then(mod => mod.BillingV2Module);
        });

        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.billing.agreements.actions.confirmDeleteAgreement',
                    {number: item.number ? item.number : ''}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe().subscribe((result) => {
            if (result) {
                this.store$.dispatch(new contactActions.DeleteContracts(item.id!));
            }
        });
    }

    /** Managers DAH */
    getManagers() {
        this.loading$.next(true);
        this.associationsService.getOrganizationManagers({organization: {id: this.data.association.id!}}).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: PageOrganizationManagerModel) => {
                this.managerDataSource.data = result.content!;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.managersTabLoaded = true;
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    deleteManager(element: OrganizationManagerModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.clients.associations.associationViewDialog.deleteManagerWarning',
                    {name: element.manager!.name}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.yes',
            }
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe((result) => {
            if (result) {
                this.loading$.next(true);

                this.associationsService.deleteOrganizationManager(element.id!).pipe(
                    map(() => {
                        this.loading$.next(false);
                        const managers = this.managerDataSource.data;
                        if (managers && managers.length) {
                            const managerIndex = managers.findIndex((item: OrganizationManagerModel) => item.id === element.id);

                            if (managerIndex !== -1) {
                                managers.splice(managerIndex, 1);
                                this.managerDataSource.data = managers;
                            }
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        this.loading$.next(false);
                        return EMPTY;
                    })
                ).subscribe();
            }
        });
    }

    addItem(currentTab: number | null, args?: any) {
        switch (currentTab) {
            case 0:
                if (args?.garage) {
                    const garageRowDialog$ = this.sharedService.openDialog(
                        GarageRowDialogComponent,
                        {...this.config.addItemDialog, maxWidth: '400px', height: 'auto', hasBackdrop: true},
                        {association: this.data.association}
                    );

                    garageRowDialog$.afterClosed().pipe(
                        take(1),
                    ).subscribe((dialogData: { result: boolean, data?: OrganizationSectionModel }) => {
                        if (dialogData.result && dialogData.data) {
                            const garageRows = this.garageRowDataSource.data;
                            if (garageRows && garageRows.length) {
                                garageRows.push(dialogData.data);
                                this.garageRowDataSource.data = garageRows;
                            } else {
                                this.garageRowDataSource.data = [dialogData.data];
                            }
                            this.changeDetectorRef.detectChanges();
                        }
                    });
                } else {
                    const addObjectDialog$ = this.sharedService.openDialog(
                        BuildingDialogComponent,
                        {...this.config.addItemDialog, maxWidth: '700px', hasBackdrop: true},
                        {
                            association: this.data.association,
                            aptUnion: !!args.aptUnion
                        }
                    );

                    addObjectDialog$.afterClosed().pipe(
                        take(1),
                    ).subscribe((dialogData) => {
                        if (dialogData.result && dialogData.data) {
                            const buildings: OrganizationBuildingModel[] = this.buildingDataSource.data;
                            const newBuilding = {...dialogData.data, name: dialogData.data.name};
                            if (buildings && buildings.length) {
                                this.buildingDataSource.data = [...buildings, newBuilding];
                                this.changeDetectorRef.detectChanges();
                            } else {
                                this.buildingDataSource.data = [newBuilding];
                            }

                            this.updateListItemLocation();
                        }
                    });
                }


                break;
            case 1:
                // eslint-disable-next-line no-case-declarations
                const associationRoleDialog$ = this.sharedService.openDialog(
                    AssociationRoleDialogComponent,
                    {...this.config.addItemDialog, maxWidth: '400px', hasBackdrop: true},
                    {
                        association: this.data.association,
                        ...args && {...args}
                    }
                );

                associationRoleDialog$.afterClosed().pipe(
                    take(1),
                ).subscribe((dialogData: { result: boolean, data?: any }) => {
                    if (dialogData?.result) {
                        this.getRoles();
                    }
                    if (this.data.association.tariff == 'BASE') {
                        this.updateAssociation();
                    }
                });
                break;
            case 3:
                // eslint-disable-next-line no-case-declarations
                const notAllowedTypes = [];
                if (this.managerDataSource?.data?.length) {
                    for (let i = 0; i < this.managerDataSource.data.length; i++) {
                        notAllowedTypes.push(this.managerDataSource.data[i].position);
                    }

                    if (notAllowedTypes.length == 4) {
                        this.toastrService.info('Працівників зі всіми ролями вже додано. Для зміни видаліть когось і додайте з новою роллю!');
                        break;
                    }
                }
                // eslint-disable-next-line no-case-declarations
                const managerDialog$ = this.sharedService.openDialog(
                    AssociationManagerDialogComponent,
                    {...this.config.addItemDialog, hasBackdrop: true},
                    {association: this.data.association, notAllowedTypes}
                );

                managerDialog$.afterClosed().pipe(
                    take(1),
                ).subscribe((dialogData: { result: boolean, data?: OrganizationManagerModel }) => {
                    if (dialogData.result && dialogData.data) {
                        const managers = this.managerDataSource.data;
                        if (managers && managers.length) {
                            managers.unshift(dialogData.data);
                            this.managerDataSource.data = managers;
                        } else {
                            this.managerDataSource.data = [dialogData.data];
                        }

                        this.changeDetectorRef.detectChanges();
                    }
                });
                break;
            default:
                return;
        }
    }

    editAssociation() {
        this.sharedService.openDialog(
            AssociationDialogComponent,
            {...this.config.addItemDialog, hasBackdrop: true},
            {
                edit: true,
                association: this.data.association
            }
        );
    }

    blockAssociation() {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'Обʼєднання буде заблоковано. Продовжити?',
                    {number: this.data.association.number ?? ''}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.yes',
            }
        );

        dialog$.afterClosed().pipe().subscribe((result) => {
            if (result && this.data.association.id) {
                this.store$.dispatch(new associationsActions.BlockAssociations({associationId: this.data.association.id}));
            }
        });
    }

    changeTariff() {
        const tariffDialog$ = this.sharedService.openDialog(
            AssociationTariffDialogComponent,
            {...this.config.addItemDialog, hasBackdrop: true},
            {association: this.data.association}
        );

        tariffDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((dialogData: { result: boolean, data?: OrganizationTariffModel }) => {
            if (dialogData.result && dialogData.data) {
                this.data.association = {...this.data.association, tariff: dialogData.data.tariff};
                this.store$.dispatch(new associationActions.EditAssociationsSuccess(this.data.association));
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    showTariffHistory() {
        this.sharedService.openDialog(
            AssociationTariffHistoryComponent,
            {...this.config.addItemDialog},
            {association: this.data.association}
        );
    }

    openCabinet() {
        this.associationsService.getTempToken(this.data.association.id!).pipe(
            takeUntil(this.destroyed$),
            map((response: OAuth2TokensDto) => {
                if (response) {
                    window.open(
                        // tslint:disable-next-line:max-line-length
                        `${environment.tempCabinetBaseUrl}/external?accessToken=${response.accessToken}&associationId=${this.data.association.id}&cabinetTypeRoute=housing-organization`, '_blank', '_blank'
                    );
                }
            })
        ).subscribe();
    }

    deleteTenants() {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.clients.associations.associationViewDialog.deleteTenantsWarning',
                    {name: this.data.association.name}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe((result) => {
            if (result) {
                this.associationsService.deleteTenants(this.data.association.id!).pipe(
                    take(1),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        return of();
                    })
                ).subscribe(() => {
                    this.toastrService.info(`Виконано`);
                });
            }
        });
    }

    deleteApartments() {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.clients.associations.associationViewDialog.deleteApartmentsWarning',
                    {name: this.data.association.name}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe((result) => {
            if (result) {
                this.associationsService.deleteApartments(this.data.association.id!).pipe(
                    take(1),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        return of();
                    })
                ).subscribe(() => {
                    this.toastrService.info(`Виконано`);
                });
            }
        });
    }

    /** Watch Add or Edit Association Action */
    watchEditAssociationSuccess() {
        this.actions$.pipe(
            ofType(associationActions.AssociationsActionTypes.EditAssociationsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: associationActions.EditAssociationsSuccess) => {
            // Оновлюємо дані перегляду, щоб показати зміни і передати їх у повторне редагування
            if (action.payload?.id === this.data.association.id) {
                this.data.association = {...this.data.association, ...action.payload};
                this.changeDetectorRef.markForCheck();
            }
            this.saved = true;
            this.loading$.next(false);
        });
    }

    watchContractActions() {
        this.actions$.pipe(
            ofType(contractActions.ContractsActionTypes.AddContractsSuccess,
                contractActions.ContractsActionTypes.EditContractsSuccess,
                contractActions.ContractsActionTypes.DeleteContractsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.getContracts();
            this.updateAssociation();
        });
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }

    /** apiToken actions */
    generateApiToken() {
        if (this.data.association?.apiToken) {
            const confirmDialog$ = this.sharedService.openDialog(
                ConfirmationDialogComponent,
                {...this.config.confirmationDialog, hasBackdrop: true},
                {
                    title: SharedService.getTranslation(
                        this.translateService,
                        `modules.clients.associations.associationViewDialog.apiToken.confirmationText.regenerate`,
                    ),
                    cancelText: 'common.buttons.no',
                    submitText: 'common.buttons.yes',
                }
            );

            confirmDialog$.afterClosed().pipe(
                take(1),
            ).subscribe((result) => {
                if (result) {
                    this.generateApiTokenRes();
                }
            });
        } else {
            this.generateApiTokenRes();
        }
    }

    generateApiTokenRes() {
        this.openApi.generateTokenApi(this.data.association.id!).pipe(
            take(1),
            map(() => {
                this.updateAssociation();
                this.changeDetectorRef.detectChanges();
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            }),
        ).subscribe();
    }

    deleteApiToken() {
        const confirmDialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    `modules.clients.associations.associationViewDialog.apiToken.confirmationText.clear`,
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        confirmDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.openApi.deleteTokenApi(this.data.association.id!).pipe(
                    take(1),
                    map(() => {
                        this.updateAssociation();
                        this.changeDetectorRef.detectChanges();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        return EMPTY;
                    }),
                ).subscribe();
            }
        });
    }

    openPolygonMap(buildingId?: string) {
        const polygonDialog$ = this.sharedService.openDialog(
            MapPolygonDialogComponent,
            {...this.config.wideDialog, hasBackdrop: true, height: '100%'}, {
                association: this.data.association,
                buildingId,
                allowInexactly: true,
            });

        polygonDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((dialogData) => {
            if (dialogData?.result && dialogData?.data) {
                if (buildingId) {
                    const buildings: OrganizationBuildingModel[] = this.buildingDataSource.data;
                    const buildingIndex: number = buildings && buildings.length ? buildings.findIndex((item: OrganizationBuildingModel) => item.id === buildingId) : -1;
                    if (buildingIndex !== -1) {
                        buildings.splice(buildingIndex, 1, {
                            ...buildings[buildingIndex],
                            location: !!dialogData.data?.polyPoints?.length,
                            locationInexactly: !!dialogData.data?.inexactly
                        });
                        this.buildingDataSource.data = buildings;
                        this.changeDetectorRef.detectChanges();
                    }
                } else {
                    this.data.association = {...this.data.association, location: !!dialogData.data?.polyPoints?.length};
                }

                this.updateListItemLocation();
            }
        });
    }

    updateListItemLocation() {
        if (this.data.association.type == 'PRIVATE_SECTOR_COOP' || this.data.association.type == 'GARAGE_COOP') {
            this.store$.dispatch(new associationActions.EditAssociationsSuccess({...this.data.association}));
        } else {
            const buildings: OrganizationBuildingModel[] = this.buildingDataSource.data;
            this.store$.dispatch(new associationActions.EditAssociationsSuccess({...this.data.association, location: buildings?.every(el => el.location)}));
        }
    }

    generateAndDownloadPNG(link: string): void {
        const canvas = this.canvas.nativeElement;

        QRCode.toCanvas(canvas, link, {width: 1000}, (error) => {
            if (error) {
                return;
            }

            const imgData = canvas.toDataURL('image/png');

            const a = document.createElement('a');
            a.href = imgData;
            a.download = 'qr-code.png';
            a.click();
        });
    }

    generateAndDownloadSVG(link: string): void {
        QRCode.toString(link, {type: 'svg'}, (err, svgString) => {
            if (err) {
                return;
            }

            const blob = new Blob([svgString], {type: 'image/svg+xml'});
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'qr-code.svg';
            a.click();

            URL.revokeObjectURL(url);
        });
    }
}
